/**
 * What happened to a campaign, while it is happening and afterwards (PLAN §12.5).
 *
 * The numbers here are three that the specification's §32 blurs together and
 * that mean genuinely different things: **accepted** is the provider taking the
 * message, **delivered** is a handset receiving it, and **failed** is neither.
 * Reporting acceptance as delivery is the single most misleading thing an SMS
 * panel can do, so they never share a box.
 *
 * **There is no poll** (PLAN §17). The old screen asked every two
 * seconds; this one reads on load and has a Refresh button. Supabase Realtime
 * is the natural answer if live updates are wanted later — a subscription on
 * the campaign row, and no polling endpoint at all — and nothing here forecloses
 * it.
 */

import { el, moment, number, replace, type Child } from '../dom.ts';
import { campaign, control, recipients, type CampaignRow, type RecipientRow } from '../data.ts';
import { csvFilename, toCsv } from '../../csv-view.ts';
import { translate as t, translateKey } from '../i18n.ts';
import { failure, link, loading, renderShell } from '../shell.ts';
import { RECIPIENT_LABELS, STATUS_LABELS, statusPill } from './status-words.ts';
import { available, paging as pagingModel } from './status-view.ts';

const PAGE = 100;

export function renderStatus(root: Element, email: string, id: number): void {
  renderShell(root, { active: 'campaigns', email, title: t('status.title', { id }) }, loading());

  // The shell's `<main>`, captured once. Everything below redraws the whole
  // screen — the figures, the table and the paging all change together — so
  // there is one container and it has to be the one that stays attached. An
  // earlier version held on to the first `<section>` instead, and a *failed*
  // reload after a successful one wrote its message into a node that had
  // already been replaced: no error shown, and a screen that looked like it had
  // simply stopped responding.
  const page = root.querySelector('.page') ?? root;

  let offset = 0;
  let filter = '';
  let search = '';

  const load = () => {
    void Promise.all([campaign(id), recipients(id, { offset, limit: PAGE, status: filter, search })])
      .then(([row, current]) => {
        if (row === undefined) {
          replace(page, el('section', { class: 'card' }, el('h1', {}, t('error.notFound'))));
          return;
        }
        replace(
          page,
          el(
            'section',
            { class: 'card' },
            heading(row),
            figures(row),
            progress(row),
            row.fatal_error_key === null ? false : fatal(row),
            actions(row, () => load()),
          ),
          el('section', { class: 'card' }, el('h2', {}, t('confirm.message')), el('pre', { class: 'message-preview' }, row.template)),
          el(
            'section',
            { class: 'card' },
            el('h2', {}, t('table.title')),
            filters(filter, (chosen) => {
              filter = chosen;
              offset = 0;
              load();
            }),
            // The two that turn a ten-thousand-row table into something a
            // person can use: find one customer, or hand the whole thing to
            // whoever does the reconciliation.
            el(
              'div',
              { class: 'table-tools' },
              searchBox(search, (typed) => {
                search = typed;
                offset = 0;
                load();
              }),
              exportButton(row, filter),
            ),
            current.rows.length === 0 && search !== ''
              ? el('p', { class: 'muted' }, t('table.searchEmpty', { query: search }))
              : table(current.rows, row.status !== 'draft'),
            paging(offset, current.rows.length, current.total, (next) => {
              offset = next;
              load();
            }),
          ),
        );
      })
      .catch(() => replace(page, el('section', { class: 'card' }, failure(load))));
  };

  load();
}

function heading(row: CampaignRow): Child {
  return el(
    'div',
    {},
    el('h1', {}, t('status.title', { id: row.id })),
    el('p', { class: statusPill(row.status) }, t(STATUS_LABELS[row.status] ?? 'status.draft')),
  );
}

function figures(row: CampaignRow): Child {
  return el(
    'div',
    { class: 'figures' },
    figure(t('status.recipients'), row.recipient_count),
    figure(t('status.accepted'), row.accepted_count),
    figure(t('status.delivered'), row.delivered_count),
    figure(t('status.failed'), row.failed_count),
  );
}

function figure(label: string, value: number): Child {
  return el('p', { class: 'figure' }, el('output', {}, number(value)), el('span', {}, label));
}

/**
 * Native `<progress>`, so it is announced and needs no styling to be legible.
 * The value is accepted plus failed: both are settled, and what is left is what
 * has not happened yet.
 */
function progress(row: CampaignRow): Child {
  return el('progress', { max: row.recipient_count, value: row.accepted_count + row.failed_count });
}

/**
 * Why the campaign stopped, in the provider's own terms.
 *
 * A configuration fault is the one kind of failure the administrator can
 * actually fix, so the message names the fix — the secret, the dashboard, the
 * top-up — rather than a code. The second sentence is the part people need
 * most: **the messages already sent are not sent again.** Without it, Resume is
 * a button nobody dares press.
 */
function fatal(row: CampaignRow): Child {
  return el(
    'div',
    { class: 'finding error' },
    el('span', {}, `${translateKey(row.fatal_error_key ?? '')} ${t('status.fatalResume')}`),
  );
}

function actions(row: CampaignRow, done: () => void): Child {
  const { stop, resume, retry, pending } = available(row);

  if (row.status === 'draft') {
    return el(
      'div',
      { class: 'actions' },
      el('a', { class: 'button-link', href: `/campaign/${row.id}/confirm`, onclick: link(`/campaign/${row.id}/confirm`) }, t('confirm.send')),
    );
  }

  const press = (action: Parameters<typeof control>[0]) => () => {
    void control(action, row.id).then(done).catch(done);
  };

  return el(
    'div',
    { class: 'actions' },
    stop && el('button', { type: 'button', class: 'danger', onclick: press('cancel_sending') }, t('status.stop')),
    resume && el('button', { type: 'button', onclick: press('resume_sending') }, t('status.resume', { pending })),
    retry &&
      el(
        'button',
        { type: 'button', class: 'secondary', onclick: press('retry_failed') },
        t('status.retryFailed', { failed: row.failed_count }),
      ),
    // A pure read — the report endpoint cannot send anything — so it is safe to
    // press at any time and any number of times. It is also the guarantee
    // behind the delivery schedule: the passes are a convenience, and this is
    // what is left once they have all been and gone.
    !stop &&
      el(
        'button',
        {
          type: 'button',
          class: 'secondary',
          onclick: () => {
            void control('refresh_delivery', row.id).then(done).catch(done);
          },
        },
        t('status.refresh'),
      ),
    el('button', { type: 'button', class: 'secondary', onclick: done }, t('app.retry')),
  );
}

function filters(current: string, choose: (status: string) => void): Child {
  const options: readonly [string, string][] = [
    ['', t('table.filter.all')],
    ['accepted', t('table.filter.accepted')],
    ['delivered', t('recipient.delivered')],
    ['failed', t('table.filter.failed')],
    ['pending', t('recipient.pending')],
  ];

  return el(
    'div',
    { class: 'filters' },
    ...options.map(([value, label]) =>
      el(
        'button',
        {
          type: 'button',
          // `aria-current` rather than a class: it is what the stylesheet
          // already keys the current filter off, and it is announced.
          'aria-current': value === current ? 'page' : undefined,
          onclick: () => choose(value),
        },
        label,
      ),
    ),
  );
}

function table(rows: readonly RecipientRow[], results: boolean): Child {
  if (rows.length === 0) return el('p', { class: 'muted' }, t('table.empty'));

  return el(
    'div',
    { class: 'table-wrap' },
    el(
    'table',
    { class: 'recipients' },
    el(
      'thead',
      {},
      el(
        'tr',
        {},
        el('th', {}, t('campaign.preview.line')),
        el('th', {}, t('campaign.preview.phone')),
        el('th', {}, t('campaign.preview.code')),
        el('th', { class: 'numeric' }, t('table.parts')),
        results && el('th', {}, t('table.status')),
        results && el('th', {}, t('table.delivery')),
        results && el('th', {}, t('table.error')),
      ),
    ),
    el('tbody', {}, ...rows.map((row) => recipientRow(row, results))),
    ),
  );
}

function recipientRow(row: RecipientRow, results: boolean): Child {
  return el(
    'tr',
    {},
    el('td', { class: 'numeric' }, row.line),
    el('td', {}, row.phone),
    el('td', {}, row.promo_code),
    // The provider's count once there is one: it is what the bill is
    // calculated from.
    el('td', { class: 'numeric' }, number(row.actual_parts ?? row.estimated_parts)),
    results && el('td', {}, t(RECIPIENT_LABELS[row.status] ?? 'recipient.pending')),
    results && el('td', {}, row.delivered_at === null ? '—' : moment(row.delivered_at)),
    // The provider's own wording where there is any: it names the actual number
    // in a list it rejected, which our codes cannot.
    results && el('td', {}, row.error_message ?? (row.error_key === null ? '' : translateKey(row.error_key))),
  );
}

function paging(offset: number, shown: number, total: number, go: (offset: number) => void): Child {
  const model = pagingModel(offset, shown, total, PAGE);
  if (!model.needed) return false;

  return el(
    'div',
    { class: 'pager' },
    el('span', {}, t('table.showing', { from: model.from, to: model.to, total })),
    el(
      'button',
      {
        type: 'button',
        class: 'secondary',
        disabled: model.previous === undefined,
        onclick: () => go(model.previous ?? 0),
      },
      t('table.previous'),
    ),
    el('span', {}, t('table.page', { page: model.page, pages: model.pages })),
    el(
      'button',
      {
        type: 'button',
        class: 'secondary',
        disabled: model.next === undefined,
        onclick: () => go(model.next ?? offset),
      },
      t('table.next'),
    ),
  );
}


/**
 * One box against two columns.
 *
 * Submitted rather than filtered as you type: every keystroke would be a
 * request against a table that can hold ten thousand rows, and the answer is
 * a page from the server rather than a filter over what is on screen — because
 * the row being looked for is usually not on the page being shown.
 */
function searchBox(current: string, choose: (query: string) => void): Child {
  const box = el('input', {
    type: 'search',
    value: current,
    placeholder: t('table.searchPlaceholder'),
    'aria-label': t('table.search'),
  });

  const form = el(
    'form',
    { class: 'search' },
    box,
    el('button', { type: 'submit', class: 'secondary' }, t('table.search')),
    current === '' ? false : el('button', { type: 'button', class: 'link-button', onclick: () => choose('') }, t('table.clear')),
  );

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    choose(box.value.trim());
  });

  return form;
}

/**
 * The whole result set as a file, not the page on screen.
 *
 * Paged out of PostgREST in the same hundreds the table uses, because the point
 * of the file is the rows nobody scrolled to. A campaign of ten thousand is a
 * hundred requests and a few seconds, and the button says so while it works.
 *
 * The download is built in the browser from rows it already has the right to
 * read — there is no export endpoint, and nothing new is exposed by this.
 */
function exportButton(row: CampaignRow, filter: string): Child {
  const button = el('button', { type: 'button', class: 'secondary' }, t('table.export'));

  button.addEventListener('click', () => {
    button.disabled = true;
    button.textContent = t('table.exporting');

    void collectAll(row.id, filter)
      .then((rows) => {
        download(csvFilename(row.id), toCsv(rows, CSV_COLUMNS));
        button.textContent = t('table.export');
      })
      .catch(() => {
        button.textContent = t('table.exportFailed');
      })
      .finally(() => {
        button.disabled = false;
      });
  });

  return button;
}

const CSV_COLUMNS = [
  { header: 'line', value: (row: RecipientRow) => row.line },
  { header: 'phone', value: (row: RecipientRow) => row.phone },
  { header: 'promo_code', value: (row: RecipientRow) => row.promo_code },
  { header: 'status', value: (row: RecipientRow) => row.status },
  { header: 'delivery_status', value: (row: RecipientRow) => row.delivery_status },
  { header: 'parts', value: (row: RecipientRow) => row.actual_parts ?? row.estimated_parts },
  { header: 'sent_at', value: (row: RecipientRow) => row.sent_at },
  { header: 'delivered_at', value: (row: RecipientRow) => row.delivered_at },
  { header: 'error', value: (row: RecipientRow) => row.error_message ?? row.error_key },
  { header: 'message', value: (row: RecipientRow) => row.final_message },
];

/**
 * Every page, in order.
 *
 * The loop stops on a short page rather than on the reported total: the total
 * is a second count and a campaign that is still being written to can return
 * one that never quite arrives, which would be a loop nobody can cancel.
 */
async function collectAll(campaignId: number, filter: string): Promise<readonly RecipientRow[]> {
  const all: RecipientRow[] = [];

  for (let offset = 0; ; offset += PAGE) {
    const page = await recipients(campaignId, { offset, limit: PAGE, status: filter });
    all.push(...page.rows);

    if (page.rows.length < PAGE) return all;
    // A ceiling, so a paging bug is a short file rather than a tab that never
    // stops asking.
    if (all.length >= 100_000) return all;
  }
}

/**
 * Hands the file to the browser.
 *
 * An object URL rather than a `data:` one — a ten thousand row export is
 * megabytes, and a `data:` URL that long is refused outright by some browsers
 * and silently truncated by others. Revoked on the next tick, because the click
 * has already happened by then and holding it leaks the whole file.
 */
function download(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const link = el('a', { href: url, download: filename });

  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
